import { mergeUserData, useAuthContext } from '@aibees/shared/auth';
import {
  FormDialogSubmitHandler,
  Icon,
  RHFTextField,
} from '@aibees/shared/components';
import { notificationIconMap } from '@aibees/shared/features/notifications/config';
import { useUserEditMutationApi } from '@aibees/shared/features/user-profile/hooks/use-user-edit-mutation-api';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Button, Stack, Typography } from '@mui/material';
import { random } from 'lodash-es';
import { memo, useMemo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { ChangeInfoDialogProps as Props } from './types';
import { useConfig } from './use-config';

export const ChangeInfoDialog = memo(({ className }: Props) => {
  const dialogState = useBoolean();
  const notify = useNotify();
  const api = useUserEditMutationApi();
  const { user, setUser } = useAuthContext();
  const config = useConfig(user);

  const dialogKey = dialogState.value ? 'open' : 'closed';

  const defaultValues = useMemo(() => {
    if (!user) return {};

    return {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      attributes: {
        title: user.attributes?.title || '',
      },
    };
  }, [user]);

  const handleSubmit: FormDialogSubmitHandler = (data) => {
    api.mutateAsync(mergeUserData(user, data)).then((res) => {
      setUser(res);
      notify.complete(
        'Your information has been updated',
        'Your information has been updated and will now be visible on your profile.',
        random(1, 100),
        {
          icon: notificationIconMap.Profile!,
        }
      );
      dialogState.onFalse();
    });
  };

  return (
    <Styled.Wrapper
      className={classnames('ChangeInfoDialog', className)}
      sx={SX.root}
    >
      <Styled.Dialog
        {...config}
        key={dialogKey}
        uncontrolledElement={
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon name="edit-outline" />}
          >
            Edit
          </Button>
        }
        title={
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Icon name="edit-outline" size={20} color="primary.400" />
            <Typography variant="body2" fontWeight={600} color="grey.800">
              Edit Information
            </Typography>
          </Stack>
        }
        onSubmit={handleSubmit}
        state={dialogState}
        defaultValues={defaultValues}
        submitButtonProps={{
          children: 'Save',
          variant: 'contained',
          color: 'primary',
        }}
        fullWidth
        maxWidth="xs"
      >
        <Stack gap={2}>
          <RHFTextField
            size={'medium'}
            name="firstName"
            label="First Name"
            placeholder="Enter first name"
            InputLabelProps={{ shrink: true }}
          />
          <RHFTextField
            size={'medium'}
            name="lastName"
            label="Last Name"
            placeholder="Enter last name"
            InputLabelProps={{ shrink: true }}
          />
          <RHFTextField
            size={'medium'}
            name="attributes.title"
            label="Title"
            placeholder="Enter title"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Styled.Dialog>
    </Styled.Wrapper>
  );
});

ChangeInfoDialog.displayName = 'ChangeInfoDialog';
